import React, { useState, useMemo, useEffect } from 'react';
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { useSelector } from 'react-redux';
import { Form, Button, Badge, Accordion, Modal } from 'react-bootstrap';
import moment from 'moment';
import EditJobModal from '../JobSchedulePage/EditJobModal';
import useWeekCalculations from '../../functions/useWeekCalculations';
import { selectPtoEntries } from '../../redux/slices/ptoSlice';
import { selectHolidays } from '../../redux/slices/holidaysSlice';
import { selectProtectedTimeRecords } from '../../redux/slices/protectedTimeSlice';
import { useEngineeringTasks } from '../../functions/useEngineeringTasks';
import { formatDateForForm, formatDateUnified } from '../../utils/dateUtils';
import { 
  HOURS_PER_DAY, 
  calculateEngineeringDueDate,
  calculateWorkingDaysBackward,
  isHoliday
} from '../../utils/engineeringCalculations';
import { calculateWeeksToShip, getWeeksToShipColor, calculateEngineeringWeeks, getEngineeringWeeksColor } from '../../utils/dateCalculations';
import './EngineeringCalendar.css';
import { memoizeCalendarCalculation } from './calendarRenderCache';

const getTaskAssignedStartDate = (task) => task?.DateAssigned || task?.StartDate || '';

const parseCalendarDate = (value) => {
  const normalizedDate = formatDateForForm(value);
  return normalizedDate ? moment(normalizedDate, 'YYYY-MM-DD', true) : null;
};

/**
 * EngineeringCalendar Component
 * 
 * Displays a Gantt-style calendar view for Engineers with:
 * 
 * JOBS (Production Work):
 * - Job assignments displayed as task bars across working days
 * - Engineering due dates calculated as Review Due Date - 2 days
 * - Start dates calculated backwards from engineering due date
 * - Use available production hours (8h/day minus protected time)
 * 
 * TASKS (Protected Time Work):
 * - Engineering tasks work FORWARD from today to show realistic completion
 * - Tasks ONLY use protected time hours (not production hours)
 * - Effort (hours) distributed across available protected time days
 * - Progress percentage reduces remaining hours to allocate
 * - Example: 40h task @ 0% progress + 8h/week protected time = spans 5 weeks
 * - Tasks automatically skip weekends, PTO, and holidays
 * - Due date cell shows "DUE" marker in yellow
 * - Task work AFTER due date shown in RED (overdue work indicator)
 * - Task work BEFORE due date shown in BLUE (on-track)
 * 
 * CAPACITY MANAGEMENT:
 * - Protected time blocks reduce available daily capacity for jobs
 * - PTO and holiday blocks (full day blockage for both jobs and tasks)
 * - Visual indicators for blocked and partially blocked days
 * - Week-based view with configurable date ranges
 * - Realistic scheduling based on current date and progress
 */
const EngineeringCalendar = () => {
  // Redux selectors
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const mechOptions = useSelector((state) => state.dropdown.userDropdowns.mechOptions);
  const elecOptions = useSelector((state) => state.dropdown.userDropdowns.elecOptions || []);
  const controlOptions = useSelector((state) => state.dropdown.userDropdowns.controlOptions || []);
  const ptoEntries = useSelector(selectPtoEntries);
  const holidays = useSelector(selectHolidays);
  const protectedTimeRecords = useSelector(selectProtectedTimeRecords);
  
  // Get current user roles for permission checking
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  
  // Calculate edit permission based on roles
  // Exclude accounting users from editing in engineering views
  const hasAnyEditPermission = (roles) => {
    return roles && roles.length > 0;
  };
  const canEdit = hasAnyEditPermission(userRoles) && !userRolesMapped.Accounting;
  
  // Engineering tasks hook
  const { tasks, fetchTasks, loading: tasksLoading } = useEngineeringTasks();
  
  // Fetch tasks on component mount
  useEffect(() => {
    console.log('EngineeringCalendar: Fetching engineering tasks');
    fetchTasks();
  }, [fetchTasks]);
  
  // Week calculations hook - get 52 weeks of data
  const { workWeekColumns } = useWeekCalculations(52);
  
  // Filter states
  const [startWeekIndex, setStartWeekIndex] = useState(0); // Start with Week 1
  const [endWeekIndex, setEndWeekIndex] = useState(11);    // Default to 12 weeks (3 months)
  const [activeDateRangeButton, setActiveDateRangeButton] = useState('3month');
  const [selectedEngineerType, setSelectedEngineerType] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showUnscheduledItems, setShowUnscheduledItems] = useState(true);
  const [viewMode, setViewMode] = useState('gantt'); // 'gantt' or 'timeline'
  const [showCompactFilters, setShowCompactFilters] = useState(false);
  const [showCompactLegend, setShowCompactLegend] = useState(false);
  const [expandedEngineers, setExpandedEngineers] = useState({});
  
  // Modal state for double-click editing
  const [showModal, setShowModal] = useState(false);
  useRealtimeDraft(showModal);
  const [selectedJob, setSelectedJob] = useState(null);
  
  /**
   * Build engineer lists by type
   */
  const mechanicalEngineers = useMemo(
    () => {
      if (!mechOptions || !Array.isArray(mechOptions)) return [];
      return mechOptions.map((u) => ({
        label: u.displayName,
        value: u.displayName,
        field: 'MechanicalEngineer',
        durField: 'MechanicalDuration',
        doneField: 'MechanicalDone',
        dueDateField: 'MechanicalDue',
      }));
    },
    [mechOptions]
  );

  const electricalEngineers = useMemo(
    () => {
      if (!elecOptions || !Array.isArray(elecOptions)) return [];
      return elecOptions.map((u) => ({
        label: u.displayName,
        value: u.displayName,
        field: 'ElectricalEngineer',
        durField: 'ElectricalDuration',
        doneField: 'ElectricalDone',
        dueDateField: 'ElectricalDue',
      }));
    },
    [elecOptions]
  );

  const controlsEngineers = useMemo(
    () => {
      if (!controlOptions || !Array.isArray(controlOptions)) return [];
      return controlOptions.map((u) => ({
        label: u.displayName,
        value: u.displayName,
        field: 'ControlsEngineer',
        durField: 'ControlsDuration',
        doneField: 'ControlsDone',
        dueDateField: 'ControlsDue',
      }));
    },
    [controlOptions]
  );
  
  /**
   * Get visible weeks based on filter
   */
  const visibleWeeks = useMemo(() => {
    if (!workWeekColumns || workWeekColumns.length === 0) return [];
    return workWeekColumns.slice(startWeekIndex, endWeekIndex + 1);
  }, [workWeekColumns, startWeekIndex, endWeekIndex]);
  
  /**
   * Check if a day is a weekend
   */
  const isWeekend = (date) => {
    const day = moment(date).day();
    return day === 0 || day === 6; // Sunday or Saturday
  };
  
  /**
   * Check if a day is a holiday
   */
  const isDayAHoliday = (date) => {
    return isHoliday(date, holidays);
  };
  
  /**
   * Get PTO entries for a specific engineer
   */
  const getEngineerPtoEntries = memoizeCalendarCalculation((engineerName) => {
    return ptoEntries.filter(entry => 
      entry.UserName === engineerName || 
      entry.engineerId === engineerName || 
      entry.EngineerName === engineerName
    );
  });
  
  /**
   * Check if a day has PTO for a specific engineer
   */
  const isDayInPtoPeriod = (date, engineerPto) => {
    if (!engineerPto || engineerPto.length === 0) return false;
    
    // Ensure we're working with a consistent date format
    const dayDate = moment(date).format('YYYY-MM-DD');
    
    return engineerPto.some(entry => {
      // Try both formatDateUnified and direct moment conversion for better compatibility
      let startDate, endDate;
      
      try {
        // Use formatDateUnified first
        startDate = formatDateUnified(entry.StartDate, 'iso');
        endDate = formatDateUnified(entry.EndDate, 'iso');
      } catch (error) {
        // Fallback to direct moment conversion if formatDateUnified fails
        startDate = moment(entry.StartDate).format('YYYY-MM-DD');
        endDate = moment(entry.EndDate).format('YYYY-MM-DD');
      }
      
      return dayDate >= startDate && dayDate <= endDate;
    });
  };
  
  /**
   * Get protected time entries for a specific engineer
   */
  const getEngineerProtectedTime = memoizeCalendarCalculation((engineerName) => {
    return protectedTimeRecords.filter(record => record.UserName === engineerName);
  });
  
  /**
   * Calculate protected hours for a single record on a specific day
   * Handles three scenarios:
   * 1. Day-only: Full day blocked if day matches
   * 2. Hours/Percent with days: Split evenly across selected days
   * 3. Hours/Percent without days: Distribute across all weekdays
   */
  const calculateProtectedHoursForRecord = (record, date) => {
    const checkDate = moment(date);
    const dayOfWeek = checkDate.format('dddd'); // "Monday", "Tuesday", etc.
    
    // Parse DayOfWeek field - can be:
    // - JSON array: ["Monday", "Tuesday"]
    // - Comma-separated string: "Monday,Tuesday"
    // - Single day string: "Monday"
    // - Array already: ["Monday", "Tuesday"]
    // - null/undefined
    let selectedDays = [];
    if (record.DayOfWeek) {
      if (Array.isArray(record.DayOfWeek)) {
        // Already an array
        selectedDays = record.DayOfWeek;
      } else if (typeof record.DayOfWeek === 'string') {
        // Try parsing as JSON first
        try {
          const parsed = JSON.parse(record.DayOfWeek);
          selectedDays = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // Not JSON - check if comma-separated
          if (record.DayOfWeek.includes(',')) {
            selectedDays = record.DayOfWeek.split(',').map(day => day.trim());
          } else {
            // Single day string
            selectedDays = [record.DayOfWeek.trim()];
          }
        }
      }
    }
    
    // Scenario 1: Day-only blocking (no hours/percent specified)
    if ((!record.HoursPerWeek && !record.WeekPercent) && selectedDays.length > 0) {
      // Full day blocked if this day is selected
      return selectedDays.includes(dayOfWeek) ? HOURS_PER_DAY : 0;
    }
    
    // Calculate hours per week from either HoursPerWeek or WeekPercent
    let hoursPerWeek = 0;
    if (record.HoursPerWeek) {
      hoursPerWeek = parseFloat(record.HoursPerWeek);
    } else if (record.WeekPercent) {
      hoursPerWeek = (parseFloat(record.WeekPercent) / 100) * 40; // 40 hour work week
    }
    
    if (hoursPerWeek === 0) return 0;
    
    // Scenario 2: Hours/Percent with specific days
    if (selectedDays.length > 0) {
      if (selectedDays.includes(dayOfWeek)) {
        return hoursPerWeek / selectedDays.length; // Split evenly
      }
      return 0;
    }
    
    // Scenario 3: Hours/Percent without specific days - distribute across all weekdays
    if (isWeekend(checkDate)) return 0;
    return hoursPerWeek / 5; // Split across 5 weekdays
  };
  
  /**
   * Get protected time hours for a specific date
   * Returns total protected hours if date falls within protected time periods
   * Multiple records stack (additive)
   */
  const getProtectedHoursForDay = (date, engineerName) => {
    const protectedRecords = getEngineerProtectedTime(engineerName);
    if (!protectedRecords.length) return 0;
    
    let totalProtectedHours = 0;
    
    protectedRecords.forEach(record => {
      // Check if date is within the protected time period
      const checkDate = moment(date);
      const hasStartDate = record.StartDate && record.StartDate !== null;
      const hasEndDate = record.EndDate && record.EndDate !== null;
      
      // If both dates exist, check if within range
      if (hasStartDate && hasEndDate) {
        const startDate = parseCalendarDate(record.StartDate);
        const endDate = parseCalendarDate(record.EndDate);
        if (!startDate || !endDate || !checkDate.isBetween(startDate, endDate, 'day', '[]')) {
          return; // Skip this record
        }
      } else if (hasStartDate && !hasEndDate) {
        // Only start date - applies from start date onwards
        const startDate = parseCalendarDate(record.StartDate);
        if (!startDate || checkDate.isBefore(startDate, 'day')) {
          return;
        }
      } else if (!hasStartDate && hasEndDate) {
        // Only end date - applies until end date
        const endDate = parseCalendarDate(record.EndDate);
        if (!endDate || checkDate.isAfter(endDate, 'day')) {
          return;
        }
      }
      // If no dates, applies to all days (perpetual)
      
      // Calculate protected hours for this specific day
      totalProtectedHours += calculateProtectedHoursForRecord(record, date);
    });
    
    return Math.min(totalProtectedHours, HOURS_PER_DAY); // Cap at 8 hours per day
  };
  
  /**
   * Get available production hours for a specific day
   * Returns 0 for weekends, holidays, and PTO days
   * Returns reduced hours based on protected time
   */
  const getAvailableHoursForDay = (date, engineerName) => {
    if (isWeekend(date)) return 0;
    
    const engineerPto = getEngineerPtoEntries(engineerName);
    if (isDayInPtoPeriod(date, engineerPto)) return 0;
    if (isDayAHoliday(date)) return 0;
    
    const protectedHours = getProtectedHoursForDay(date, engineerName);
    return Math.max(0, HOURS_PER_DAY - protectedHours);
  };
  
  /**
   * Helper to get the day name from a moment object
   */
  const getDayName = (momentDate) => {
    return momentDate.format('ddd');
  };
  
  /**
   * Helper to get day of month from a moment object
   */
  const getDayOfMonth = (momentDate) => {
    return momentDate.format('D');
  };
  
  /**
   * Calculate the day columns for displaying a job on the Gantt chart
   * Works backwards from engineering due date accounting for protected time
   */
  const getJobDayColumns = memoizeCalendarCalculation((job, durField, dueDateField, engineerName, engineerField) => {
    if (!job[dueDateField]) return [];
    
    // Calculate engineering due date from review due date
    // Only apply 2-day adjustment for Mechanical Engineers
    const reviewDueDate = moment(job[dueDateField]);
    let engineeringDueDate;
    
    if (engineerField === 'MechanicalEngineer') {
      // Mechanical: Review Due - 2 working days
      engineeringDueDate = calculateEngineeringDueDate(
        reviewDueDate,
        2,
        {
          holidays,
          ptoEntries,
          engineerName
        }
      );
    } else {
      // Electrical & Controls: Use due date as-is
      engineeringDueDate = reviewDueDate.clone();
    }
    
    // Get duration in days (default to 1 if not specified)
    // Keep fractional values to properly handle partial day jobs (e.g., 0.25 days = 2 hours)
    const durationDays = job[durField] ? parseFloat(job[durField]) : 1;
    
    // Use utility function to calculate working days backward
    return calculateWorkingDaysBackward(
      engineeringDueDate,
      durationDays,
      engineerName,
      {
        isWeekend,
        isDayInPtoPeriod,
        isDayAHoliday,
        getAvailableHoursForDay,
        getProtectedHoursForDay,
        getEngineerPtoEntries,
        safetyLimit: 100
      }
    );
  });
  
  /**
   * Calculate the day columns for displaying a task on the Gantt chart
   * Works FORWARD from today/StartDate to calculate realistic completion date
   * Shows when task can actually be completed based on available protected time
   * @returns {Object} { dayColumns: [], projectedDueDate: moment, needsReschedule: boolean, calculatedStartDate: moment }
   */
  const getTaskDayColumns = memoizeCalendarCalculation((task, engineerName) => {
    if (!task.DueDate) return { dayColumns: [], projectedDueDate: null, needsReschedule: false, calculatedStartDate: null };
    
    // Engineering tasks use their actual task due date. Do not apply the
    // production job review buffer here.
    const originalDueDate = parseCalendarDate(task.DueDate);
    if (!originalDueDate) {
      return { dayColumns: [], projectedDueDate: null, needsReschedule: false, calculatedStartDate: null };
    }
    const columns = [];
    
    // Get engineer's PTO entries for blocking
    const engineerPto = getEngineerPtoEntries(engineerName);
    
    // If no Effort specified, show as 1-day task on due date (if protected time available)
    if (!task.Effort) {
      if (!isWeekend(originalDueDate) && 
          !isDayInPtoPeriod(originalDueDate, engineerPto) && 
          !isDayAHoliday(originalDueDate)) {
        const protectedHours = getProtectedHoursForDay(originalDueDate, engineerName);
        if (protectedHours > 0) {
          columns.push(originalDueDate.format('YYYY-MM-DD'));
        }
      }
      return {
        dayColumns: columns,
        projectedDueDate: originalDueDate,
        needsReschedule: false,
        calculatedStartDate: columns.length > 0 ? originalDueDate : null
      };
    }
    
    // Calculate remaining hours based on progress
    const effortHours = parseFloat(task.Effort) || 0;
    const progress = parseFloat(task.Progress) || 0;
    const remainingHours = effortHours * (1 - progress / 100);
    
    // If task is complete, return empty
    if (remainingHours <= 0) {
      return { dayColumns: [], projectedDueDate: originalDueDate, needsReschedule: false, calculatedStartDate: null };
    }
    
    // Work forward from today (or the assigned start date if it is in the future).
    let currentDate = moment().startOf('day');
    const assignedStartDate = getTaskAssignedStartDate(task);
    const specifiedStartDate = parseCalendarDate(assignedStartDate);
    
    // If an assigned start date is in the future, start from there.
    if (specifiedStartDate && specifiedStartDate.isAfter(currentDate, 'day')) {
      currentDate = specifiedStartDate.clone();
    }
    
    const safetyLimit = 365; // Check up to 1 year forward
    let daysChecked = 0;
    let hoursAllocated = 0;
    let calculatedStartDate = null;
    
    // Collect days working forward
    const daysWithHours = [];
    
    while (hoursAllocated < remainingHours && daysChecked < safetyLimit) {
      daysChecked++;
      
      // Skip weekends, PTO, and holidays
      if (!isWeekend(currentDate) && 
          !isDayInPtoPeriod(currentDate, engineerPto) && 
          !isDayAHoliday(currentDate)) {
        
        // Get protected hours available for this day
        const protectedHours = getProtectedHoursForDay(currentDate, engineerName);
        
        if (protectedHours > 0) {
          // Consume up to the available protected hours
          const hoursToUse = Math.min(remainingHours - hoursAllocated, protectedHours);
          daysWithHours.push({
            date: currentDate.format('YYYY-MM-DD'),
            hours: hoursToUse
          });
          hoursAllocated += hoursToUse;
          
          // Set calculated start date on first day with hours
          if (!calculatedStartDate) {
            calculatedStartDate = currentDate.clone();
          }
        }
      }
      
      // Move forward one day
      currentDate.add(1, 'day');
    }
    
    // Projected due date is the last day in the schedule
    const projectedDueDate = daysWithHours.length > 0 
      ? moment(daysWithHours[daysWithHours.length - 1].date)
      : null;
    
    // Check if projected due date is after original due date
    const needsReschedule = projectedDueDate && projectedDueDate.isAfter(originalDueDate, 'day');
    
    return {
      dayColumns: daysWithHours.map(d => d.date),
      projectedDueDate,
      needsReschedule,
      calculatedStartDate
    };
  });
  
  /**
   * Group jobs by engineer
   */
  const groupJobsByEngineer = (engineers) => {
    const grouped = {};
    
    engineers.forEach((eng) => {
      const engineerJobs = jobScheduleData.filter((job) => {
        // Match engineer by field
        return job[eng.field] === eng.value;
      });
      
      if (engineerJobs.length > 0) {
        grouped[eng.value] = engineerJobs;
      }
    });
    
    return grouped;
  };

  const mechanicalJobs = groupJobsByEngineer(mechanicalEngineers);
  const electricalJobs = groupJobsByEngineer(electricalEngineers);
  const controlsJobs = groupJobsByEngineer(controlsEngineers);
  
  /**
   * All tasks - no filtering by ShowOnSchedule
   */
  const scheduleTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    return tasks; // Show all tasks
  }, [tasks]);
  
  /**
   * Group tasks by engineer
   */
  const groupTasksByEngineer = (engineers) => {
    const grouped = {};
    
    engineers.forEach((eng) => {
      const engineerTasks = scheduleTasks.filter(task => 
        task.AssignedTo === eng.value && 
        getTaskAssignedStartDate(task) &&
        task.Status !== "Completed" // Hide completed tasks
      );
      if (engineerTasks.length > 0) {
        grouped[eng.value] = engineerTasks;
      }
    });
    
    return grouped;
  };
  
  const mechanicalTasks = groupTasksByEngineer(mechanicalEngineers);
  const electricalTasks = groupTasksByEngineer(electricalEngineers);
  const controlsTasks = groupTasksByEngineer(controlsEngineers);
  
  /**
   * Check if a day is blocked (PTO or Holiday) for visual indicators
   * Returns: 'pto', 'holiday', or false
   */
  const isDayBlocked = (day, engineerPto) => {
    if (isDayInPtoPeriod(day, engineerPto)) return 'pto';
    if (isDayAHoliday(day)) return 'holiday';
    return false;
  };
  
  /**
   * Apply preset week range
   */
  const applyPresetRange = (weeks, buttonKey) => {
    setStartWeekIndex(0);
    setEndWeekIndex(weeks - 1);
    setActiveDateRangeButton(buttonKey);
  };
  
  /**
   * Handle start week change
   */
  const handleStartWeekChange = (newIndex) => {
    const numericIndex = parseInt(newIndex, 10);
    if (numericIndex >= 0 && numericIndex < workWeekColumns.length) {
      setStartWeekIndex(numericIndex);
      setActiveDateRangeButton(''); // Clear active button when manually adjusted
      
      // Ensure end week is after start week
      if (numericIndex > endWeekIndex) {
        setEndWeekIndex(numericIndex);
      }
    }
  };
  
  /**
   * Handle end week change
   */
  const handleEndWeekChange = (newIndex) => {
    const numericIndex = parseInt(newIndex, 10);
    if (numericIndex >= 0 && numericIndex < workWeekColumns.length && numericIndex >= startWeekIndex) {
      setEndWeekIndex(numericIndex);
      setActiveDateRangeButton(''); // Clear active button when manually adjusted
    }
  };
  
  /**
   * Get relative week label
   */
  const getRelativeWeekLabel = (weekIndex) => {
    const week = workWeekColumns[weekIndex];
    return week ? week.label : `Week ${weekIndex + 1}`;
  };
  
  /**
   * Handle double-click on job row to open edit modal
   */
  const handleRowDoubleClick = (job) => {
    if (canEdit) {
      setSelectedJob(job);
      setShowModal(true);
    }
  };

  /**
   * Close the edit modal
   */
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  /**
   * Handle save changes from modal
   */
  const handleSaveChanges = () => {
    handleCloseModal();
  };
  
  /**
   * Render the Gantt chart for a list of engineers (with jobs and tasks)
   */
  const renderDailyAccordion = (engineers, jobsByEng, tasksByEng, header) => (
    <>
      <h3 className="mt-4 mb-3">{header}</h3>
      <Accordion
        className="engineering-calendar-rows"
        activeKey={expandedEngineers[header] || []}
        onSelect={(keys) => setExpandedEngineers(previous => ({ ...previous, [header]: keys || [] }))}
        alwaysOpen
      >
        {engineers.map((eng) => {
          const jobs = jobsByEng[eng.value] || [];
          if (!jobs.length) return null;
          
          // Filter jobs based on criteria
          const filteredAndSortedJobs = jobs.filter((job) => {
            // Skip if job is done and we're not showing completed
            if (!showCompleted && job[eng.doneField]) {
              return false;
            }
            
            // Skip jobs with status "Shipped"
            if (job.JobStatus === "Shipped" || job.ActualShipDate) {
              return false;
            }
            
            // If showUnscheduledItems is enabled, include all assigned items
            if (showUnscheduledItems) {
              return true;
            }
            
            // Apply date range filter
            if (job[eng.dueDateField]) {
              const reviewDueDate = moment(job[eng.dueDateField]);
              
              // Calculate engineering due date (only adjust for Mechanical)
              let engineeringDueDate;
              if (eng.field === 'MechanicalEngineer') {
                // Mechanical: Review Due - 2 working days
                engineeringDueDate = calculateEngineeringDueDate(
                  reviewDueDate,
                  2,
                  { holidays, ptoEntries, engineerName: eng.value }
                );
              } else {
                // Electrical & Controls: Use due date as-is
                engineeringDueDate = reviewDueDate.clone();
              }
              
              if (workWeekColumns && workWeekColumns.length > 0) {
                const startWeek = workWeekColumns[startWeekIndex];
                const endWeek = workWeekColumns[endWeekIndex];
                
                if (startWeek && endWeek) {
                  const firstDay = moment(startWeek.startDate);
                  const lastDay = moment(endWeek.endDate);
                  return engineeringDueDate.isBetween(firstDay, lastDay, 'day', '[]');
                }
              }
            }
            
            return job[eng.dueDateField]; // Must have due date
          }).sort((a, b) => {
            // Sort by due date (earliest first)
            if (!a[eng.dueDateField]) return 1;
            if (!b[eng.dueDateField]) return -1;
            return new Date(a[eng.dueDateField]) - new Date(b[eng.dueDateField]);
          });
          
          // Find past due items
          const pastDueJobs = jobs.filter((job) => {
            if (job[eng.doneField]) return false;
            if (job.JobStatus === "Shipped") return false;
            
            if (job[eng.dueDateField] && workWeekColumns.length > 0) {
              const reviewDueDate = moment(job[eng.dueDateField]);
              // Only adjust for Mechanical Engineers
              let engineeringDueDate;
              if (eng.field === 'MechanicalEngineer') {
                engineeringDueDate = calculateEngineeringDueDate(
                  reviewDueDate,
                  2,
                  { holidays, ptoEntries, engineerName: eng.value }
                );
              } else {
                engineeringDueDate = reviewDueDate.clone();
              }
              const firstWeek = workWeekColumns[startWeekIndex];
              if (firstWeek) {
                const firstDay = moment(firstWeek.startDate);
                return engineeringDueDate.isBefore(firstDay, 'day');
              }
            }
            
            return false;
          }).sort((a, b) => {
            return new Date(a[eng.dueDateField]) - new Date(b[eng.dueDateField]);
          });
          
          // Combine past due jobs and filtered jobs
          const allJobsToShow = [...pastDueJobs, ...filteredAndSortedJobs.filter(job => !pastDueJobs.includes(job))];
          
          if (!allJobsToShow.length) return null;
          
          // Get PTO and protected time information for this engineer
          const engineerPto = getEngineerPtoEntries(eng.value);
          const isExpanded = (expandedEngineers[header] || []).includes(eng.value);
          
          // Pre-calculate total hours per day for this engineer (performance optimization)
          const dayHoursMap = new Map();
          if (isExpanded) allJobsToShow.forEach(job => {
            const jobDayColumns = getJobDayColumns(job, eng.durField, eng.dueDateField, eng.value, eng.field);
            jobDayColumns.forEach(dc => {
              const dayKey = dc.date.format('YYYY-MM-DD');
              dayHoursMap.set(dayKey, (dayHoursMap.get(dayKey) || 0) + dc.hours);
            });
          });
          
          return (
            <Accordion.Item className="engineering-calendar-engineer-row" eventKey={eng.value} key={eng.value}>
              <Accordion.Header>
                <div className="engineering-calendar-row-heading d-flex justify-content-between align-items-center w-100">
                  <div className="engineering-calendar-row-identity">
                    <span className="engineering-calendar-engineer-name">{eng.label}</span>
                    {engineerPto.length > 0 && (
                      <Badge bg="warning" className="ms-2">
                        {engineerPto.length} PTO period{engineerPto.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  <div className="engineering-calendar-row-counts">
                    <Badge bg="secondary" pill>
                      {allJobsToShow.length} project{allJobsToShow.length !== 1 ? 's' : ''}
                    </Badge>
                    {pastDueJobs.length > 0 && (
                      <Badge bg="danger" className="ms-1">{pastDueJobs.length} past due</Badge>
                    )}
                  </div>
                </div>
              </Accordion.Header>
              {isExpanded && <Accordion.Body>
                <div className="engineering-calendar-scroll-hint" role="note">
                  Swipe horizontally to view release dates, due dates, and the daily schedule.
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered mb-5 engineering-gantt-table">
                    <thead>
                      <tr>
                        <th className="gantt-job-number" style={{width: '125px'}}>Job Number</th>
                        <th className="gantt-project-name" style={{width: '300px'}}>Project Name</th>
                        <th style={{width: '100px'}}>Long Leads</th>
                        <th style={{width: '100px'}}>Release Date</th>
                        <th style={{width: '100px'}}>Duration (days)</th>
                        <th className="engineering-due-date" style={{width: '100px'}}>Engineering Due</th>
                        {visibleWeeks.map((week) => (
                          <React.Fragment key={week.weekNum}>
                            <th colSpan="5" className="text-center week-header">
                              {week.label}
                            </th>
                          </React.Fragment>
                        ))}
                      </tr>
                      <tr>
                        <th className="gantt-job-number" colSpan="1"></th>
                        <th className="gantt-project-name" colSpan="1"></th>
                        <th colSpan="3"></th>
                        <th></th>
                        {visibleWeeks.map((week) => {
                          // Calculate the 5 weekdays for this work week
                          const weekdays = [];
                          for (let i = 0; i < 5; i++) {
                            const day = moment(week.startDate).add(i, 'days');
                            weekdays.push(day);
                          }
                          
                          return weekdays.map((day, idx) => {
                            // Check if day is blocked or has protected time
                            const isBlocked = isDayBlocked(day, engineerPto);
                            const protectedHours = getProtectedHoursForDay(day, eng.value);
                            const hasPartialProtection = protectedHours > 0 && protectedHours < HOURS_PER_DAY;
                            const hasFullProtection = protectedHours >= HOURS_PER_DAY;
                            const blockClass = isBlocked ? (isBlocked === 'pto' ? 'pto-day' : 'holiday-day') : '';
                            
                            return (
                              <th key={`${week.weekNum}-${idx}`} 
                                  className={`text-center day-column ${blockClass} ${hasPartialProtection ? 'partial-protection' : ''} ${hasFullProtection ? 'full-protection' : ''}`} 
                                  style={{width: '50px', position: 'relative'}}>
                                {getDayName(day)}<br />{getDayOfMonth(day)}
                                {hasPartialProtection && (
                                  <div style={{fontSize: '9px', color: '#666'}}>
                                    {HOURS_PER_DAY - protectedHours}h
                                  </div>
                                )}
                              </th>
                            );
                          });
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Render past due items first if any */}
                      {pastDueJobs.length > 0 && (
                        <>
                          <tr className="bg-danger text-white">
                            <td colSpan={5 + (visibleWeeks.length * 5)} className="text-center">
                              <strong>Past Due Items</strong>
                            </td>
                          </tr>
                          {pastDueJobs.map((job) => {
                            const dayColumns = getJobDayColumns(job, eng.durField, eng.dueDateField, eng.value, eng.field);
                            const reviewDueDate = job[eng.dueDateField] ? moment(job[eng.dueDateField]) : null;
                            let engineeringDueDate = null;
                            if (reviewDueDate) {
                              if (eng.field === 'MechanicalEngineer') {
                                engineeringDueDate = calculateEngineeringDueDate(reviewDueDate, 2, { holidays, ptoEntries, engineerName: eng.value });
                              } else {
                                engineeringDueDate = reviewDueDate.clone();
                              }
                            }
                            
                            // Calculate weeks to ship
                            const weeksToShip = calculateWeeksToShip(job.CurrentPlanShipDate);
                            const weeksToShipColor = getWeeksToShipColor(weeksToShip);
                            const engineeringWeeks = calculateEngineeringWeeks(job[eng.dueDateField], job.CurrentPlanShipDate);
                            const engineeringWeeksColor = getEngineeringWeeksColor(engineeringWeeks);
                            
                            return (
                              <tr key={job.ID} className="table-danger" onDoubleClick={() => handleRowDoubleClick(job)} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
                                <td className="gantt-job-number">{job.JobNumber}</td>
                                <td className="gantt-project-name">{job.JobName || job.ProjectName}</td>
                                <td>
                                  {eng.field === 'MechanicalEngineer' && job.MechanicalLongLeads?.startsWith("Yes") && (
                                    <span>
                                      {job.MechanicalLongLeads}
                                      {job.MechanicalLongLeadsDone ? (
                                        <div className="text-success small">
                                          <i className="bi bi-check-circle me-1"></i>
                                          {moment(job.MechanicalLongLeadsDone).format('MM/DD/YYYY')}
                                        </div>
                                      ) : (
                                        <div className="text-warning small">Pending</div>
                                      )}
                                    </span>
                                  )}
                                  {eng.field === 'ElectricalEngineer' && job.ElectricalLongLeads?.startsWith("Yes") && (
                                    <span>
                                      {job.ElectricalLongLeads}
                                      {job.ElectricalLongLeadsDone ? (
                                        <div className="text-success small">
                                          <i className="bi bi-check-circle me-1"></i>
                                          {moment(job.ElectricalLongLeadsDone).format('MM/DD/YYYY')}
                                        </div>
                                      ) : (
                                        <div className="text-warning small">Pending</div>
                                      )}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {job.ProductionRelease
                                    ? moment(job.ProductionRelease).format('MM/DD')
                                    : ''}
                                </td>
                                <td>{job[eng.durField] || 1}</td>
                                <td className="engineering-due-date">
                                  {engineeringDueDate ? (
                                    <>
                                      <div className="fw-bold">{engineeringDueDate.format('MM/DD')}</div>
                                      <div className="small text-danger">PAST DUE</div>
                                      {weeksToShip !== null && job.CurrentPlanShipDate && (
                                        <div className={`small ${weeksToShipColor}`}>
                                          Ships in {weeksToShip > 0 ? '' : ''}{weeksToShip} wks
                                        </div>
                                      )}
                                      {engineeringWeeks !== null && job.CurrentPlanShipDate && (
                                        <div className={`small ${engineeringWeeksColor}`}>
                                          {engineeringWeeks > 0 ? '+' : ''}{engineeringWeeks} eng wks
                                        </div>
                                      )}
                                    </>
                                  ) : ''}
                                </td>
                                
                                {/* Render task bars across days */}
                                {visibleWeeks.map((week) => {
                                  const weekdays = [];
                                  for (let i = 0; i < 5; i++) {
                                    const day = moment(week.startDate).add(i, 'days');
                                    weekdays.push(day);
                                  }
                                  
                                  return weekdays.map((day) => {
                                    // Check if this day is in the task
                                    const dayColumn = dayColumns.find(dc => dc.date.isSame(day, 'day'));
                                    const isBlocked = isDayBlocked(day, engineerPto);
                                    const protectedHours = getProtectedHoursForDay(day, eng.value);
                                    
                                    // Check for overbooking using pre-calculated map
                                    const dayKey = day.format('YYYY-MM-DD');
                                    const totalDayHours = dayHoursMap.get(dayKey) || 0;
                                    const isOverbooked = totalDayHours > HOURS_PER_DAY;
                                    
                                    if (isBlocked) {
                                      const blockClass = isBlocked === 'pto' ? 'pto-day' : 'holiday-day';
                                      return <td key={day.format('YYYY-MM-DD')} className={blockClass}>X</td>;
                                    }
                                    
                                    if (dayColumn) {
                                      const fillPercent = (dayColumn.hours / HOURS_PER_DAY) * 100;
                                      return (
                                        <td key={day.format('YYYY-MM-DD')} 
                                            className="task-day"
                                            style={{
                                              background: isOverbooked 
                                                ? `linear-gradient(to top, #dc3545 100%, transparent 0%)`
                                                : `linear-gradient(to top, #0d6efd ${fillPercent}%, transparent ${fillPercent}%)`,
                                              position: 'relative',
                                              border: isOverbooked ? '2px solid #dc3545' : 'none',
                                              boxShadow: isOverbooked ? '0 0 5px rgba(220, 53, 69, 0.5)' : 'none'
                                            }}
                                            title={isOverbooked 
                                              ? `⚠️ OVERBOOKED: ${totalDayHours.toFixed(1)}h scheduled (${(totalDayHours - HOURS_PER_DAY).toFixed(1)}h over capacity)` 
                                              : `${dayColumn.hours.toFixed(1)}h / ${dayColumn.availableHours}h available`}>
                                          {protectedHours > 0 && (
                                            <div style={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              height: '20%',
                                              background: 'repeating-linear-gradient(45deg, #ffc107, #ffc107 2px, transparent 2px, transparent 4px)',
                                              opacity: 0.5
                                            }}></div>
                                          )}
                                        </td>
                                      );
                                    }
                                    
                                    return <td key={day.format('YYYY-MM-DD')}></td>;
                                  });
                                })}
                              </tr>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Render regular jobs */}
                      {filteredAndSortedJobs.filter(job => !pastDueJobs.includes(job)).map((job) => {
                        const dayColumns = getJobDayColumns(job, eng.durField, eng.dueDateField, eng.value, eng.field);
                        const reviewDueDate = job[eng.dueDateField] ? moment(job[eng.dueDateField]) : null;
                        let engineeringDueDate = null;
                        if (reviewDueDate) {
                          if (eng.field === 'MechanicalEngineer') {
                            engineeringDueDate = calculateEngineeringDueDate(reviewDueDate, 2, { holidays, ptoEntries, engineerName: eng.value });
                          } else {
                            engineeringDueDate = reviewDueDate.clone();
                          }
                        }
                        
                        // Calculate weeks to ship
                        const weeksToShip = calculateWeeksToShip(job.CurrentPlanShipDate);
                        const weeksToShipColor = getWeeksToShipColor(weeksToShip);
                        const engineeringWeeks = calculateEngineeringWeeks(job[eng.dueDateField], job.CurrentPlanShipDate);
                        const engineeringWeeksColor = getEngineeringWeeksColor(engineeringWeeks);
                        
                        return (
                          <tr key={job.ID} className={job[eng.doneField] ? 'table-success' : ''} onDoubleClick={() => handleRowDoubleClick(job)} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
                            <td className="gantt-job-number">{job.JobNumber}</td>
                            <td className="gantt-project-name">{job.JobName || job.ProjectName}</td>
                            <td>
                              {eng.field === 'MechanicalEngineer' && job.MechanicalLongLeads?.startsWith("Yes") && (
                                <span>
                                  {job.MechanicalLongLeads}
                                  {job.MechanicalLongLeadsDone ? (
                                    <div className="text-success small">
                                      <i className="bi bi-check-circle me-1"></i>
                                      {moment(job.MechanicalLongLeadsDone).format('MM/DD/YYYY')}
                                    </div>
                                  ) : (
                                    <div className="text-warning small">Pending</div>
                                  )}
                                </span>
                              )}
                              {eng.field === 'ElectricalEngineer' && job.ElectricalLongLeads?.startsWith("Yes") && (
                                <span>
                                  {job.ElectricalLongLeads}
                                  {job.ElectricalLongLeadsDone ? (
                                    <div className="text-success small">
                                      <i className="bi bi-check-circle me-1"></i>
                                      {moment(job.ElectricalLongLeadsDone).format('MM/DD/YYYY')}
                                    </div>
                                  ) : (
                                    <div className="text-warning small">Pending</div>
                                  )}
                                </span>
                              )}
                            </td>
                            <td>
                              {job.ProductionRelease
                                ? moment(job.ProductionRelease).format('MM/DD')
                                : ''}
                            </td>
                            <td>{job[eng.durField] || 1}</td>
                            <td className="engineering-due-date">
                              {engineeringDueDate ? (
                                <>
                                  <div>{engineeringDueDate.format('MM/DD')}</div>
                                  {weeksToShip !== null && job.CurrentPlanShipDate && (
                                    <div className={`small ${weeksToShipColor}`}>
                                      Ships in {weeksToShip > 0 ? '+' : ''}{weeksToShip} wks
                                    </div>
                                  )}
                                  {engineeringWeeks !== null && job.CurrentPlanShipDate && (
                                    <div className={`small ${engineeringWeeksColor}`}>
                                      {engineeringWeeks > 0 ? '+' : ''}{engineeringWeeks} eng wks
                                    </div>
                                  )}
                                </>
                              ) : ''}
                            </td>
                            
                            {/* Render task bars across days */}
                            {visibleWeeks.map((week) => {
                              const weekdays = [];
                              for (let i = 0; i < 5; i++) {
                                const day = moment(week.startDate).add(i, 'days');
                                weekdays.push(day);
                              }
                              
                              return weekdays.map((day) => {
                                // Check if this day is in the task
                                const dayColumn = dayColumns.find(dc => dc.date.isSame(day, 'day'));
                                const isBlocked = isDayBlocked(day, engineerPto);
                                const protectedHours = getProtectedHoursForDay(day, eng.value);
                                
                                // Check for overbooking using pre-calculated map
                                const dayKey = day.format('YYYY-MM-DD');
                                const totalDayHours = dayHoursMap.get(dayKey) || 0;
                                const isOverbooked = totalDayHours > HOURS_PER_DAY;
                                
                                if (isBlocked) {
                                  const blockClass = isBlocked === 'pto' ? 'pto-day' : 'holiday-day';
                                  return <td key={day.format('YYYY-MM-DD')} className={blockClass}>X</td>;
                                }
                                
                                if (dayColumn) {
                                  const fillPercent = (dayColumn.hours / HOURS_PER_DAY) * 100;
                                  return (
                                    <td key={day.format('YYYY-MM-DD')} 
                                        className="task-day"
                                        style={{
                                          background: isOverbooked 
                                            ? `linear-gradient(to top, #dc3545 100%, transparent 0%)`
                                            : `linear-gradient(to top, #28a745 ${fillPercent}%, transparent ${fillPercent}%)`,
                                          position: 'relative',
                                          border: isOverbooked ? '2px solid #dc3545' : 'none',
                                          boxShadow: isOverbooked ? '0 0 5px rgba(220, 53, 69, 0.5)' : 'none'
                                        }}
                                        title={isOverbooked 
                                          ? `⚠️ OVERBOOKED: ${totalDayHours.toFixed(1)}h scheduled (${(totalDayHours - HOURS_PER_DAY).toFixed(1)}h over capacity)` 
                                          : `${dayColumn.hours.toFixed(1)}h / ${dayColumn.availableHours}h available`}>
                                      {protectedHours > 0 && (
                                        <div style={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          height: '20%',
                                          background: 'repeating-linear-gradient(45deg, #ffc107, #ffc107 2px, transparent 2px, transparent 4px)',
                                          opacity: 0.5
                                        }}></div>
                                      )}
                                    </td>
                                  );
                                }
                                
                                // Empty cell - check if job spans across this protected time day
                                if (protectedHours >= HOURS_PER_DAY && dayColumns.length > 0) {
                                  // Check if there's work scheduled before or after this day
                                  const hasPriorWork = dayColumns.some(dc => dc.date.isBefore(day, 'day'));
                                  const hasFutureWork = dayColumns.some(dc => dc.date.isAfter(day, 'day'));
                                  
                                  if (hasPriorWork && hasFutureWork) {
                                    return (
                                      <td key={day.format('YYYY-MM-DD')} 
                                          style={{ position: 'relative' }}
                                          title={`Full-day protected time - Job spans across`}>
                                        <div style={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          height: '20%',
                                          background: 'repeating-linear-gradient(45deg, #ffc107, #ffc107 2px, transparent 2px, transparent 4px)',
                                          opacity: 0.5
                                        }}></div>
                                      </td>
                                    );
                                  }
                                }
                                
                                // Empty cell - check if job spans across this protected time day
                                if (protectedHours >= HOURS_PER_DAY && dayColumns.length > 0) {
                                  // Check if there's work scheduled before or after this day
                                  const hasPriorWork = dayColumns.some(dc => dc.date.isBefore(day, 'day'));
                                  const hasFutureWork = dayColumns.some(dc => dc.date.isAfter(day, 'day'));
                                  
                                  if (hasPriorWork && hasFutureWork) {
                                    return (
                                      <td key={day.format('YYYY-MM-DD')} 
                                          style={{ position: 'relative' }}
                                          title={`Full-day protected time - Job spans across`}>
                                        <div style={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          height: '20%',
                                          background: 'repeating-linear-gradient(45deg, #ffc107, #ffc107 2px, transparent 2px, transparent 4px)',
                                          opacity: 0.5
                                        }}></div>
                                      </td>
                                    );
                                  }
                                }
                                
                                return <td key={day.format('YYYY-MM-DD')}></td>;
                              });
                            })}
                          </tr>
                        );
                      })}
                      
                      {/* Render engineering tasks - completed within protected time */}
                      {(() => {
                        const engineerTasks = tasksByEng[eng.value] || [];
                        if (engineerTasks.length === 0) return null;
                        
                        return (
                          <>
                            <tr className="bg-info text-white">
                              <td colSpan={6 + (visibleWeeks.length * 5)} className="text-center">
                                <strong>Engineering Tasks (Protected Time)</strong>
                              </td>
                            </tr>
                            <tr>
                              <th className="gantt-job-number" style={{width: '125px'}}>Badge</th>
                              <th className="gantt-project-name" style={{width: '300px'}} colSpan={2}>Title</th>
                              <th style={{width: '100px'}}>Start Date</th>
                              <th style={{width: '100px'}}>Duration (hours)</th>
                              <th style={{width: '100px'}}>Due Date</th>
                              {visibleWeeks.map((week) => (
                                <React.Fragment key={`task-week-${week.weekNum}`}>
                                  <th colSpan="5" className="text-center week-header">
                                    {week.label}
                                  </th>
                                </React.Fragment>
                              ))}
                            </tr>
                            {engineerTasks.map((task) => {
                              const taskSchedule = getTaskDayColumns(task, eng.value);
                              const { dayColumns: taskDayColumns, calculatedStartDate } = taskSchedule;
                              
                              const originalDueDate = parseCalendarDate(task.DueDate);
                              const isPastDue = originalDueDate && originalDueDate.isBefore(moment(), 'day');
                              const progress = task.Progress ? parseFloat(task.Progress) : 0;
                              
                              return (
                                <tr key={`task-${task.ID}`} className={isPastDue ? 'table-warning' : ''}>
                                  <td className="gantt-job-number">
                                    <Badge bg="info">TASK</Badge>
                                  </td>
                                  <td className="gantt-project-name" colSpan={2}>
                                    <div style={{fontSize: '0.9em'}}>
                                      {task.Title}
                                      {task.Priority && (
                                        <Badge 
                                          bg={require('../../utils/priorityBadgeConfig').PRIORITY_BADGE_VARIANTS[task.Priority] || 'secondary'} 
                                          className="ms-2"
                                        >
                                          {task.Priority}
                                        </Badge>
                                      )}
                                      {progress > 0 && (
                                        <Badge bg="success" className="ms-2">
                                          {progress}%
                                        </Badge>
                                      )}
                                    </div>
                                  </td>
                                  <td title="Calculated from protected time availability">
                                    {calculatedStartDate ? calculatedStartDate.format('MM/DD/YYYY') : '-'}
                                  </td>
                                  <td>
                                    {task.Effort ? `${task.Effort}h` : '-'}
                                  </td>
                                  <td>
                                    {originalDueDate ? originalDueDate.format('MM/DD/YYYY') : 'No due date'}
                                  </td>
                                  
                                  {/* Render task bars across days */}
                                  {visibleWeeks.map((week) => {
                                    const weekdays = [];
                                    for (let i = 0; i < 5; i++) {
                                      const day = moment(week.startDate).add(i, 'days');
                                      weekdays.push(day);
                                    }
                                    
                                    return weekdays.map((day, idx) => {
                                      const dayStr = day.format('YYYY-MM-DD');
                                      const isInTask = taskDayColumns.includes(dayStr);
                                      const isOriginalDue = originalDueDate && day.isSame(originalDueDate, 'day');
                                      const isAfterDueDate = originalDueDate && day.isAfter(originalDueDate, 'day');
                                      const isOverdue = isInTask && isAfterDueDate;
                                      
                                      if (isInTask) {
                                        return (
                                          <td key={dayStr} style={{ 
                                            backgroundColor: isOverdue ? '#f8d7da' : '#d1ecf1', 
                                            borderLeft: isOverdue ? '2px solid #dc3545' : '2px solid #0dcaf0',
                                            borderRight: isOverdue ? '2px solid #dc3545' : '2px solid #0dcaf0',
                                            position: 'relative'
                                          }}>
                                            {isOriginalDue && (
                                              <div style={{
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                color: '#856404',
                                                textAlign: 'center'
                                              }}>
                                                DUE
                                              </div>
                                            )}
                                          </td>
                                        );
                                      }
                                      
                                      // Show DUE marker on due date even if no task work that day
                                      if (isOriginalDue) {
                                        return (
                                          <td key={dayStr} style={{ position: 'relative', backgroundColor: '#fff3cd' }}>
                                            <div 
                                              style={{
                                                fontSize: '10px',
                                                color: '#856404',
                                                textAlign: 'center',
                                                fontWeight: 'bold'
                                              }}
                                              title={`Due date for ${task.Title}`}
                                            >
                                              DUE
                                            </div>
                                          </td>
                                        );
                                      }
                                      
                                      return <td key={dayStr}></td>;
                                    });
                                  })}
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </Accordion.Body>}
            </Accordion.Item>
          );
        })}
      </Accordion>
    </>
  );
  
  // Check if data is loaded
  const hasEngineerData = (mechOptions && mechOptions.length > 0) || 
                          (elecOptions && elecOptions.length > 0) || 
                          (controlOptions && controlOptions.length > 0);
  
  const hasJobData = jobScheduleData && jobScheduleData.length > 0;
  
  // Check loading states from Redux
  const jobScheduleLoading = useSelector((state) => state.jobSchedule.loading);
  const dropdownLoading = useSelector((state) => state.dropdown.loading);
  
  // Determine if we're still waiting for critical data
  const hasWorkWeekColumns = Array.isArray(workWeekColumns) && workWeekColumns.length > 0;
  const isLoading =
    (jobScheduleLoading && !hasJobData) ||
    (dropdownLoading && !hasEngineerData) ||
    !hasWorkWeekColumns;
  
  // Debug logging
  useEffect(() => {
    console.log('EngineeringCalendar - Data Status:', {
      mechEngineers: mechanicalEngineers.length,
      elecEngineers: electricalEngineers.length,
      controlEngineers: controlsEngineers.length,
      jobs: jobScheduleData?.length || 0,
      tasks: tasks?.length || 0,
      holidays: holidays?.length || 0,
      protectedTime: protectedTimeRecords?.length || 0,
      ptoEntries: ptoEntries?.length || 0,
      workWeeks: workWeekColumns?.length || 0,
      jobScheduleLoading,
      dropdownLoading,
      tasksLoading,
      isLoading
    });
  }, [mechanicalEngineers, electricalEngineers, controlsEngineers, jobScheduleData, tasks, holidays, protectedTimeRecords, ptoEntries, workWeekColumns, jobScheduleLoading, dropdownLoading, tasksLoading, isLoading]);
  
  /**
   * Render Weekly Overview - shows engineers grouped by week
   */
  const renderWeeklyOverview = () => {
    const visibleWeeks = workWeekColumns.slice(startWeekIndex, endWeekIndex + 1);
    
    // Combine all engineers
    const allEngineers = [
      ...mechanicalEngineers.map(e => ({...e, type: 'Mechanical'})),
      ...electricalEngineers.map(e => ({...e, type: 'Electrical'})),
      ...controlsEngineers.map(e => ({...e, type: 'Controls'}))
    ].filter(eng => {
      if (selectedEngineerType === 'all') return true;
      if (selectedEngineerType === 'mechanical') return eng.type === 'Mechanical';
      if (selectedEngineerType === 'electrical') return eng.type === 'Electrical';
      if (selectedEngineerType === 'controls') return eng.type === 'Controls';
      return true;
    });
    
    return (
      <>
        {visibleWeeks.map((week) => {
          const weekStart = moment(week.startDate);
          const weekEnd = moment(week.endDate);
          
          // For each engineer, find jobs that have work during this week
          const engineersWithJobs = [];
          const engineersWithoutJobs = [];
          
          allEngineers.forEach(eng => {
            const allJobs = [
              ...(mechanicalJobs[eng.value] || []),
              ...(electricalJobs[eng.value] || []),
              ...(controlsJobs[eng.value] || [])
            ];
            
            // Get tasks for this engineer
            const allTasks = [
              ...(mechanicalTasks[eng.value] || []),
              ...(electricalTasks[eng.value] || []),
              ...(controlsTasks[eng.value] || [])
            ];
            
            // Filter jobs that overlap with this week
            const weekJobs = allJobs.filter(job => {
              if (!showCompleted && job[eng.doneField]) return false;
              if (job.JobStatus === "Shipped") return false;
              
              if (!job[eng.dueDateField]) return false;
              
              // Get the job's day columns to see if any fall in this week
              const dayColumns = getJobDayColumns(job, eng.durField, eng.dueDateField, eng.value);
              
              // Check if any day column falls within this week
              const hasWorkThisWeek = dayColumns.some(dc => 
                dc.date.isBetween(weekStart, weekEnd, 'day', '[]')
              );
              
              return hasWorkThisWeek;
            });
            
            // Filter tasks that overlap with this week
            const weekTasks = allTasks.filter(task => {
              if (!getTaskAssignedStartDate(task) || (!task.Effort && !task.Duration)) return false;
              
              const taskDayColumns = getTaskDayColumns(task, eng.value).dayColumns || [];
              
              // Check if any task day falls within this week
              const hasTaskThisWeek = taskDayColumns.some(dayStr => {
                const taskDay = moment(dayStr);
                return taskDay.isBetween(weekStart, weekEnd, 'day', '[]');
              });
              
              return hasTaskThisWeek;
            });
            
            // Calculate hours for this week
            let weekHours = 0;
            weekJobs.forEach(job => {
              const dayColumns = getJobDayColumns(job, eng.durField, eng.dueDateField, eng.value);
              dayColumns.forEach(dc => {
                if (dc.date.isBetween(weekStart, weekEnd, 'day', '[]')) {
                  weekHours += dc.hours;
                }
              });
            });
            
            // Calculate task hours for this week (tasks use protected time)
            let weekTaskHours = 0;
            weekTasks.forEach(task => {
              const taskSchedule = getTaskDayColumns(task, eng.value);
              const taskDayColumns = taskSchedule.dayColumns || [];
              if (taskDayColumns.length === 0) return;
              
              // Use Effort (hours) if available, otherwise estimate from date range
              const taskEffortHours = task.Effort ? parseFloat(task.Effort) : taskDayColumns.length * HOURS_PER_DAY;
              const taskProgress = task.Progress ? parseFloat(task.Progress) : 0;
              const remainingHours = taskEffortHours * (1 - taskProgress / 100);
              const taskHoursPerDay = remainingHours / taskDayColumns.length;
              
              taskDayColumns.forEach(dayStr => {
                const taskDay = moment(dayStr);
                if (taskDay.isBetween(weekStart, weekEnd, 'day', '[]')) {
                  weekTaskHours += taskHoursPerDay;
                }
              });
            });
            
            // Check for PTO/holidays this week
            const engineerPto = getEngineerPtoEntries(eng.value);
            let ptoDays = 0;
            for (let i = 0; i < 5; i++) {
              const day = moment(weekStart).add(i, 'days');
              if (isDayInPtoPeriod(day, engineerPto) || isDayAHoliday(day)) {
                ptoDays++;
              }
            }
            
            // Calculate available hours for this week
            let availableHours = 0;
            for (let i = 0; i < 5; i++) {
              const day = moment(weekStart).add(i, 'days');
              availableHours += getAvailableHoursForDay(day, eng.value);
            }
            
            if (weekJobs.length > 0 || weekTasks.length > 0) {
              engineersWithJobs.push({
                engineer: eng,
                jobs: weekJobs,
                tasks: weekTasks,
                weekHours,
                weekTaskHours,
                availableHours,
                ptoDays
              });
            } else {
              engineersWithoutJobs.push({
                engineer: eng,
                jobs: [],
                tasks: [],
                weekHours: 0,
                weekTaskHours: 0,
                availableHours,
                ptoDays
              });
            }
          });
          
          return (
            <div key={week.weekNum} className="card mb-4">
              <div className="card-header bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    Week {week.weekNum} ({week.isoWeekNum}) • {weekStart.format('MMM D')} - {weekEnd.format('MMM D, YYYY')}
                  </h4>
                  <div>
                    <Badge bg="light" text="dark" className="fs-6">
                      {engineersWithJobs.length} busy • {engineersWithoutJobs.length} available
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                {/* Table View */}
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{width: '150px'}}>Engineer</th>
                        <th style={{width: '80px'}}>Type</th>
                        <th className="text-center" style={{width: '80px'}}>Capacity</th>
                        <th className="text-center" style={{width: '100px'}}>Hours</th>
                        <th style={{minWidth: '300px'}}>Mon</th>
                        <th style={{minWidth: '300px'}}>Tue</th>
                        <th style={{minWidth: '300px'}}>Wed</th>
                        <th style={{minWidth: '300px'}}>Thu</th>
                        <th style={{minWidth: '300px'}}>Fri</th>
                        <th>Projects</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Engineers with work */}
                      {engineersWithJobs.map(({engineer, jobs, tasks, weekHours, weekTaskHours, availableHours, ptoDays}) => {
                        const utilization = availableHours > 0 ? (weekHours / availableHours) * 100 : 0;
                        const utilizationColor = utilization > 100 ? 'danger' : utilization > 80 ? 'warning' : 'success';
                        
                        return (
                          <tr key={engineer.value} className="table-success">
                            <td><strong>{engineer.label}</strong></td>
                            <td>
                              <Badge bg="secondary">{engineer.type}</Badge>
                              {ptoDays > 0 && <Badge bg="warning" className="ms-1">{ptoDays}d PTO</Badge>}
                            </td>
                            <td className="text-center">
                              <Badge bg={utilizationColor}>{utilization.toFixed(0)}%</Badge>
                            </td>
                            <td className="text-center text-muted small">
                              <div>{weekHours.toFixed(1)}h jobs</div>
                              {weekTaskHours > 0 && <div style={{fontSize: '0.85em', color: '#0dcaf0'}}>{weekTaskHours.toFixed(1)}h tasks</div>}
                              <div style={{borderTop: '1px solid #dee2e6', paddingTop: '2px', marginTop: '2px'}}>
                                / {availableHours.toFixed(0)}h
                              </div>
                            </td>
                            
                            {/* Day columns with mini gantt */}
                            {[0, 1, 2, 3, 4].map(dayOffset => {
                              const day = moment(weekStart).add(dayOffset, 'days');
                              
                              // Calculate hours for this day with detailed job info
                              let dayHours = 0;
                              const dayJobDetails = [];
                              jobs.forEach(job => {
                                const dayColumns = getJobDayColumns(job, engineer.durField, engineer.dueDateField, engineer.value);
                                const dayColumn = dayColumns.find(dc => dc.date.isSame(day, 'day'));
                                if (dayColumn) {
                                  dayHours += dayColumn.hours;
                                  const jobDuration = job[engineer.durField] ? parseFloat(job[engineer.durField]) : 1;
                                  dayJobDetails.push({
                                    number: job.JobNumber,
                                    name: job.JobName || job.ProjectName,
                                    hours: dayColumn.hours,
                                    totalDuration: jobDuration
                                  });
                                }
                              });
                              
                              // Calculate task hours for this day
                              let dayTaskHours = 0;
                              const dayTaskDetails = [];
                              tasks.forEach(task => {
                                const taskSchedule = getTaskDayColumns(task, engineer.value);
                                const taskDayColumns = taskSchedule.dayColumns || [];
                                const needsReschedule = taskSchedule.needsReschedule || false;
                                const projectedDueDate = taskSchedule.projectedDueDate;
                                
                                if (taskDayColumns.length === 0) return;
                                
                                // Use Effort (hours) if available
                                const taskEffortHours = task.Effort ? parseFloat(task.Effort) : taskDayColumns.length * HOURS_PER_DAY;
                                const taskProgress = task.Progress ? parseFloat(task.Progress) : 0;
                                const remainingHours = taskEffortHours * (1 - taskProgress / 100);
                                const taskHoursPerDay = remainingHours / taskDayColumns.length;
                                
                                if (taskDayColumns.includes(day.format('YYYY-MM-DD'))) {
                                  dayTaskHours += taskHoursPerDay;
                                  dayTaskDetails.push({
                                    title: task.Title,
                                    hours: taskHoursPerDay,
                                    priority: task.Priority,
                                    progress: taskProgress,
                                    needsReschedule: needsReschedule,
                                    originalDueDate: task.DueDate,
                                    projectedDueDate: projectedDueDate
                                  });
                                }
                              });
                              
                              const isBlocked = isDayInPtoPeriod(day, getEngineerPtoEntries(engineer.value)) || isDayAHoliday(day);
                              const hasWork = dayHours > 0;
                              const hasTasks = dayTaskHours > 0;
                              const isOverbooked = dayHours > HOURS_PER_DAY;
                              const fillPercent = Math.min((dayHours / HOURS_PER_DAY) * 100, 100);
                              
                              // Build detailed tooltip
                              let tooltipText = '';
                              if (isBlocked) {
                                tooltipText = 'Blocked (PTO/Holiday)';
                              } else if (hasWork || hasTasks) {
                                tooltipText = `${day.format('ddd M/D')} - ${dayHours.toFixed(1)}h production`;
                                if (hasTasks) {
                                  tooltipText += ` + ${dayTaskHours.toFixed(1)}h tasks`;
                                }
                                if (isOverbooked) {
                                  tooltipText += ` ⚠️ OVERBOOKED by ${(dayHours - HOURS_PER_DAY).toFixed(1)}h!`;
                                }
                                tooltipText += '\n\n';
                                if (dayJobDetails.length > 0) {
                                  tooltipText += 'PRODUCTION JOBS:\n';
                                  dayJobDetails.forEach(detail => {
                                    tooltipText += `${detail.number}: ${detail.hours.toFixed(1)}h (${detail.totalDuration}d total)\n${detail.name}\n\n`;
                                  });
                                }
                                if (dayTaskDetails.length > 0) {
                                  tooltipText += '\nTASKS (PROTECTED TIME):\n';
                                  dayTaskDetails.forEach(detail => {
                                    tooltipText += `${detail.title}: ${detail.hours.toFixed(1)}h`;
                                    if (detail.priority) tooltipText += ` [${detail.priority}]`;
                                    if (detail.progress > 0) tooltipText += ` - ${detail.progress}% complete`;
                                    if (detail.needsReschedule) {
                                      tooltipText += ` ⚠️ OVERDUE - needs reschedule`;
                                      if (detail.projectedDueDate) {
                                        tooltipText += ` to ${moment(detail.projectedDueDate).format('MM/DD/YY')}`;
                                      }
                                    }
                                    tooltipText += '\n\n';
                                  });
                                }
                              } else {
                                tooltipText = `${day.format('ddd M/D')} - Available (${HOURS_PER_DAY}h)`;
                              }
                              
                              return (
                                <td key={dayOffset} className="p-1">
                                  <div 
                                    style={{
                                      height: '40px',
                                      border: isOverbooked ? '2px solid #dc3545' : hasTasks ? '1px solid #0dcaf0' : '1px solid #ddd',
                                      borderRadius: '3px',
                                      background: isBlocked 
                                        ? 'repeating-linear-gradient(45deg, #dc3545, #dc3545 2px, transparent 2px, transparent 4px)'
                                        : isOverbooked
                                        ? `linear-gradient(to top, #dc3545 100%, #dc3545 100%)`
                                        : hasWork
                                        ? `linear-gradient(to top, #28a745 ${fillPercent}%, #f8f9fa ${fillPercent}%)`
                                        : '#f8f9fa',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '11px',
                                      padding: '2px',
                                      cursor: 'help',
                                      boxShadow: isOverbooked ? '0 0 5px rgba(220, 53, 69, 0.5)' : hasTasks ? '0 0 3px rgba(13, 202, 240, 0.5)' : 'none',
                                      position: 'relative'
                                    }}
                                    title={tooltipText}
                                  >
                                    {hasTasks && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '20%',
                                        background: 'repeating-linear-gradient(90deg, #0dcaf0, #0dcaf0 3px, transparent 3px, transparent 6px)',
                                        borderTopLeftRadius: '3px',
                                        borderTopRightRadius: '3px'
                                      }}></div>
                                    )}
                                    {isBlocked ? (
                                      <strong style={{color: 'white'}}>X</strong>
                                    ) : hasWork || hasTasks ? (
                                      <>
                                        <strong style={{color: isOverbooked || fillPercent > 50 ? 'white' : '#666'}}>
                                          {dayHours.toFixed(1)}h
                                        </strong>
                                        <div style={{fontSize: '9px', color: isOverbooked || fillPercent > 50 ? 'white' : '#999'}}>
                                          {dayJobDetails.length} job{dayJobDetails.length !== 1 ? 's' : ''}
                                          {hasTasks && <span style={{color: '#0dcaf0', fontWeight: 'bold'}}> +T</span>}
                                        </div>
                                        {isOverbooked && (
                                          <div style={{fontSize: '10px', fontWeight: 'bold', color: 'white'}}>
                                            ⚠️
                                          </div>
                                        )}
                                      </>
                                    ) : null}
                                  </div>
                                </td>
                              );
                            })}
                            
                            <td className="small">
                              {jobs.map((job, idx) => (
                                <span key={job.ID}>
                                  <strong className="text-primary">{job.JobNumber}</strong>
                                  {idx < jobs.length - 1 && ', '}
                                </span>
                              ))}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Engineers without work */}
                      {engineersWithoutJobs.map(({engineer, availableHours, ptoDays}) => (
                        <tr key={engineer.value} className="table-light">
                          <td><span className="text-muted">{engineer.label}</span></td>
                          <td><Badge bg="secondary">{engineer.type}</Badge></td>
                          <td className="text-center">
                            {ptoDays > 0 ? (
                              <Badge bg="warning">{ptoDays}d PTO</Badge>
                            ) : (
                              <Badge bg="success">Available</Badge>
                            )}
                          </td>
                          <td className="text-center text-muted small">
                            0 / {availableHours.toFixed(0)}h
                          </td>
                          {[0, 1, 2, 3, 4].map(dayOffset => {
                            const day = moment(weekStart).add(dayOffset, 'days');
                            const isBlocked = isDayInPtoPeriod(day, getEngineerPtoEntries(engineer.value)) || isDayAHoliday(day);
                            
                            return (
                              <td key={dayOffset} className="p-1">
                                <div 
                                  style={{
                                    height: '40px',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    background: isBlocked 
                                      ? 'repeating-linear-gradient(45deg, #dc3545, #dc3545 2px, transparent 2px, transparent 4px)'
                                      : '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title={isBlocked ? 'Blocked (PTO/Holiday)' : 'Available'}
                                >
                                  {isBlocked && <strong style={{color: 'white'}}>X</strong>}
                                </div>
                              </td>
                            );
                          })}
                          <td className="text-muted small">No assignments</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {engineersWithJobs.length === 0 && engineersWithoutJobs.length === 0 && (
                  <div className="alert alert-info m-3">
                    No engineers match the current filters for this week.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  /**
   * Main render
   */
  return (
    <div className="engineering-calendar-container">
      <div className="engineering-calendar-heading d-flex justify-content-between align-items-center mb-3">
        <h2>Engineering Calendar {viewMode === 'gantt' ? '- Gantt View' : '- Weekly View'}</h2>
        
        {/* View Mode Toggle */}
        <div className="engineering-calendar-view-toggle btn-group" role="group">
          <Button 
            variant={viewMode === 'timeline' ? 'primary' : 'outline-primary'}
            onClick={() => setViewMode('timeline')}
            size="sm"
          >
            Weekly View
          </Button>
          <Button 
            variant={viewMode === 'gantt' ? 'primary' : 'outline-primary'}
            onClick={() => setViewMode('gantt')}
            size="sm"
          >
            Gantt View
          </Button>
        </div>
      </div>
      
      
      {/* Loading State */}
      {isLoading && (
        <div className="alert alert-warning d-flex align-items-center">
          <div className="spinner-border spinner-border-sm me-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>
            <strong>Loading calendar data...</strong>
            <div className="small mt-1">
              {jobScheduleLoading && <div>• Fetching job schedule data...</div>}
              {dropdownLoading && <div>• Loading engineer assignments...</div>}
              {(!workWeekColumns || workWeekColumns.length === 0) && <div>• Calculating work weeks...</div>}
            </div>
          </div>
        </div>
      )}
      
      {/* No Data Messages */}
      {!isLoading && !hasEngineerData && (
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>No engineer data available.</strong> Please ensure engineers are configured in the system.
          <div className="small mt-2">
            <strong>Troubleshooting:</strong>
            <ul className="mb-0 mt-1">
              <li>Verify engineers are set up in Microsoft Entra ID with proper groups</li>
              <li>Check that engineer names match the system configuration</li>
              <li>Try refreshing the page or logging out and back in</li>
            </ul>
          </div>
        </div>
      )}
      
      {!isLoading && hasEngineerData && !hasJobData && (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          <strong>No jobs scheduled.</strong> Jobs will appear here once they are assigned to engineers with due dates.
          <div className="small mt-2">
            Engineers are loaded but no jobs have engineering assignments yet. Visit the Job Schedule page to assign jobs to engineers.
          </div>
        </div>
      )}
      
      {/* Filters - Only show if data is loaded */}
      {!isLoading && hasEngineerData && hasJobData && (
      <div className="card mb-3 engineering-calendar-filters">
        <Button
          variant="outline-primary"
          className="engineering-calendar-compact-toggle"
          onClick={() => setShowCompactFilters((isOpen) => !isOpen)}
          aria-expanded={showCompactFilters}
          aria-controls="engineering-calendar-filter-panel"
        >
          <i className="bi bi-funnel me-2" aria-hidden="true"></i>
          Filters
          <i className={`bi ${showCompactFilters ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`} aria-hidden="true"></i>
        </Button>
        <div
          id="engineering-calendar-filter-panel"
          className={`card-body engineering-calendar-compact-panel ${showCompactFilters ? 'is-open' : ''}`}
        >
          <div className="row g-3">
            {/* Date Range Preset Buttons */}
            <div className="col-12">
              <label className="form-label fw-bold">Date Range</label>
              <div className="engineering-calendar-range-buttons btn-group" role="group">
                <Button 
                  variant={activeDateRangeButton === '1month' ? 'primary' : 'outline-primary'}
                  onClick={() => applyPresetRange(4, '1month')}
                >
                  1 Month
                </Button>
                <Button 
                  variant={activeDateRangeButton === '3month' ? 'primary' : 'outline-primary'}
                  onClick={() => applyPresetRange(12, '3month')}
                >
                  3 Months
                </Button>
                <Button 
                  variant={activeDateRangeButton === '6month' ? 'primary' : 'outline-primary'}
                  onClick={() => applyPresetRange(26, '6month')}
                >
                  6 Months
                </Button>
                <Button 
                  variant={activeDateRangeButton === '1year' ? 'primary' : 'outline-primary'}
                  onClick={() => applyPresetRange(52, '1year')}
                >
                  1 Year
                </Button>
              </div>
            </div>
            
            {/* Manual Week Selection */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Start Week</Form.Label>
                <Form.Select value={startWeekIndex} onChange={(e) => handleStartWeekChange(e.target.value)}>
                  {workWeekColumns.map((week, idx) => (
                    <option key={idx} value={idx}>
                      {getRelativeWeekLabel(idx)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>End Week</Form.Label>
                <Form.Select value={endWeekIndex} onChange={(e) => handleEndWeekChange(e.target.value)}>
                  {workWeekColumns.map((week, idx) => (
                    <option key={idx} value={idx} disabled={idx < startWeekIndex}>
                      {getRelativeWeekLabel(idx)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            
            {/* Engineer Type Filter */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Engineer Type</Form.Label>
                <Form.Select value={selectedEngineerType} onChange={(e) => setSelectedEngineerType(e.target.value)}>
                  <option value="all">All Engineers</option>
                  <option value="mechanical">Mechanical Only</option>
                  <option value="electrical">Electrical Only</option>
                  <option value="controls">Controls Only</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            {/* Show Completed Checkbox */}
            <div className="col-md-6">
              <Form.Group className="mt-4">
                <Form.Check 
                  type="checkbox"
                  label="Show Completed Jobs"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
                <Form.Check 
                  type="checkbox"
                  label="Show Unscheduled Items"
                  checked={showUnscheduledItems}
                  onChange={(e) => setShowUnscheduledItems(e.target.checked)}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
      )}
      
      {/* Legend - Only show if data is loaded */}
      {!isLoading && hasEngineerData && hasJobData && (
      <div className="card mb-3 engineering-calendar-legend">
        <Button
          variant="outline-primary"
          className="engineering-calendar-compact-toggle"
          onClick={() => setShowCompactLegend((isOpen) => !isOpen)}
          aria-expanded={showCompactLegend}
          aria-controls="engineering-calendar-legend-panel"
        >
          <i className="bi bi-palette me-2" aria-hidden="true"></i>
          Legend
          <i className={`bi ${showCompactLegend ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`} aria-hidden="true"></i>
        </Button>
        <div
          id="engineering-calendar-legend-panel"
          className={`card-body engineering-calendar-compact-panel ${showCompactLegend ? 'is-open' : ''}`}
        >
          <h6>Legend:</h6>
          <div className="engineering-calendar-legend-items d-flex gap-3 flex-wrap">
            <div>
              <span className="badge bg-success">Green Bar</span> - On-time job
            </div>
            <div>
              <span className="badge bg-danger">Red Bar</span> - Past due job
            </div>
            <div>
              <span className="badge bg-info">Blue Bar</span> - Engineering task (protected time)
            </div>
            <div>
              <span className="badge bg-danger">Red Bar</span> - Task work after due date (overdue)
            </div>
            <div>
              <span style={{
                display: 'inline-block',
                width: '30px',
                height: '20px',
                border: '1px solid #0dcaf0',
                background: 'repeating-linear-gradient(90deg, #0dcaf0, #0dcaf0 3px, transparent 3px, transparent 6px)',
                verticalAlign: 'middle',
                marginRight: '5px'
              }}></span> - Task indicator (top stripe)
            </div>
            <div>
              <span style={{
                display: 'inline-block',
                padding: '2px 4px',
                fontSize: '10px',
                fontWeight: 'bold',
                backgroundColor: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffc107',
                marginRight: '5px'
              }}>DUE</span> - Task due date marker
            </div>
            <div>
              <span style={{
                display: 'inline-block',
                width: '30px',
                height: '20px',
                backgroundColor: '#ffebee',
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(239, 83, 80, 0.2) 2px, rgba(239, 83, 80, 0.2) 4px)',
                border: '1px solid #ef5350',
                verticalAlign: 'middle',
                marginRight: '5px'
              }}></span> - PTO day (blocked)
            </div>
            <div>
              <span style={{
                display: 'inline-block',
                width: '30px',
                height: '20px',
                backgroundColor: '#e3f2fd',
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(33, 150, 243, 0.2) 2px, rgba(33, 150, 243, 0.2) 4px)',
                border: '1px solid #2196f3',
                verticalAlign: 'middle',
                marginRight: '5px'
              }}></span> - Holiday (blocked)
            </div>
            <div>
              <span style={{
                display: 'inline-block',
                width: '20px',
                height: '15px',
                background: 'repeating-linear-gradient(45deg, #ffc107, #ffc107 2px, transparent 2px, transparent 4px)',
                verticalAlign: 'middle',
                marginRight: '5px'
              }}></span> - Protected time overlay
            </div>
            <div>
              <span style={{fontSize: '10px'}}>4h</span> - Available hours (when protected time applies)
            </div>
          </div>
        </div>
      </div>
      )}
      
      {/* Render selected view mode - Only show if data is loaded */}
      {!isLoading && hasEngineerData && hasJobData && viewMode === 'gantt' ? (
        <>
          {/* Gantt Charts by Engineer Type */}
          {selectedEngineerType === 'mechanical' || selectedEngineerType === 'all' ? (
            renderDailyAccordion(mechanicalEngineers, mechanicalJobs, mechanicalTasks, 'Mechanical Engineers')
          ) : null}
          
          {selectedEngineerType === 'electrical' || selectedEngineerType === 'all' ? (
            renderDailyAccordion(electricalEngineers, electricalJobs, electricalTasks, 'Electrical Engineers')
          ) : null}
          
          {selectedEngineerType === 'controls' || selectedEngineerType === 'all' ? (
            renderDailyAccordion(controlsEngineers, controlsJobs, controlsTasks, 'Controls Engineers')
          ) : null}
        </>
      ) : !isLoading && hasEngineerData && hasJobData ? (
        <>
          {/* Weekly Overview */}
          {renderWeeklyOverview()}
        </>
      ) : null}

      {/* Edit modal for double-click on rows */}
      <Modal className="job-schedule-modal" show={showModal} onHide={handleCloseModal} size="xl">
        <EditJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseModal}
          handleSaveChanges={handleSaveChanges}
          userRoles={userRoles}
        />
      </Modal>
    </div>
  );
};

export default React.memo(EngineeringCalendar);
